import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../../config/api';
import './Credentials.css';

interface Credential {
  id: string;
  user_id: string;
  exchange: string;
  label: string;
  fee_tier: string;
  ip_whitelist: string[] | null;
  is_valid: boolean;
  created_at: string;
  last_used_at: string | null;
}

const Credentials: React.FC = () => {
  const { t } = useTranslation();
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);

  // Form fields
  const [exchange, setExchange] = useState('bybit');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [label, setLabel] = useState('');
  const [feeTier, setFeeTier] = useState('regular');

  useEffect(() => {
    fetchCredentials();
  }, []);

  const fetchCredentials = async () => {
    try {
      const response = await fetch(getApiUrl('/api/credentials'), {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setCredentials(data || []);
      }
    } catch (error) {
      console.error('Failed to fetch credentials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await fetch(getApiUrl('/api/credentials'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          exchange,
          api_key: apiKey,
          api_secret: apiSecret,
          label,
          fee_tier: feeTier,
        }),
      });

      if (response.ok) {
        // Reset form
        setExchange('bybit');
        setApiKey('');
        setApiSecret('');
        setLabel('');
        setFeeTier('regular');
        setShowForm(false);
        // Refresh list
        fetchCredentials();
      } else {
        const error = await response.text();
        alert(`Failed to save credential: ${error}`);
      }
    } catch (error) {
      console.error('Failed to save credential:', error);
      alert('Failed to save credential');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string, credLabel: string) => {
    if (!window.confirm(`Delete credential "${credLabel}"? This cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(getApiUrl(`/api/credentials/${id}`), {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        fetchCredentials();
      } else {
        const error = await response.text();
        alert(`Failed to delete credential: ${error}`);
      }
    } catch (error) {
      console.error('Failed to delete credential:', error);
      alert('Failed to delete credential');
    }
  };

  const handleVerify = async (id: string) => {
    setVerifyingId(id);
    try {
      const response = await fetch(getApiUrl(`/api/credentials/${id}/verify`), {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        const result = await response.json();
        alert(result.valid ? 'Credential is valid!' : `Invalid: ${result.error || 'Unknown error'}`);
        fetchCredentials();
      } else {
        const error = await response.text();
        alert(`Verification failed: ${error}`);
      }
    } catch (error) {
      console.error('Failed to verify credential:', error);
      alert('Failed to verify credential');
    } finally {
      setVerifyingId(null);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getExchangeBadgeClass = (exchange: string) => {
    switch (exchange) {
      case 'bybit': return 'exchange-badge bybit';
      case 'mexc': return 'exchange-badge mexc';
      case 'okx': return 'exchange-badge okx';
      default: return 'exchange-badge';
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Exchange Credentials</h1>
          <p style={{ color: 'var(--text-secondary)', marginTop: '8px' }}>
            Loading...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Exchange Credentials</h1>
        <button
          className="btn-primary"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancel' : '+ Add API Key'}
        </button>
      </div>

      {showForm && (
        <div className="card credential-form">
          <h2>Add New API Key</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Exchange</label>
                <select value={exchange} onChange={(e) => setExchange(e.target.value)} required>
                  <option value="bybit">Bybit</option>
                  <option value="mexc">MEXC</option>
                  <option value="okx">OKX</option>
                </select>
              </div>

              <div className="form-group">
                <label>Label</label>
                <input
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="My Bybit Account"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>API Key</label>
                <input
                  type="text"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Enter your API key"
                  required
                />
              </div>

              <div className="form-group">
                <label>API Secret</label>
                <input
                  type="password"
                  value={apiSecret}
                  onChange={(e) => setApiSecret(e.target.value)}
                  placeholder="Enter your API secret"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Fee Tier</label>
                <select value={feeTier} onChange={(e) => setFeeTier(e.target.value)} required>
                  <option value="regular">Regular (0.1%)</option>
                  <option value="vip1">VIP 1 (0.08%)</option>
                  <option value="vip2">VIP 2 (0.06%)</option>
                  <option value="vip3">VIP 3+ (0.04%)</option>
                </select>
              </div>
            </div>

            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Saving...' : 'Save Credential'}
            </button>
          </form>
        </div>
      )}

      {credentials.length === 0 ? (
        <div className="card empty-state">
          <h3>No API Keys Yet</h3>
          <p>Add your exchange API keys to start trading with automated bots.</p>
          <button className="btn-primary" onClick={() => setShowForm(true)}>
            + Add Your First API Key
          </button>
        </div>
      ) : (
        <div className="credentials-list">
          {credentials.map((cred) => (
            <div key={cred.id} className="card credential-card">
              <div className="credential-header">
                <div className="credential-info">
                  <span className={getExchangeBadgeClass(cred.exchange)}>
                    {cred.exchange.toUpperCase()}
                  </span>
                  <h3>{cred.label}</h3>
                </div>
                <div className="credential-status">
                  <span className={`status-dot ${cred.is_valid ? 'valid' : 'invalid'}`}></span>
                  <span className="status-text">{cred.is_valid ? 'Valid' : 'Invalid'}</span>
                </div>
              </div>

              <div className="credential-details">
                <div className="detail-item">
                  <span className="detail-label">Fee Tier:</span>
                  <span className="detail-value">{cred.fee_tier}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Created:</span>
                  <span className="detail-value">{formatDate(cred.created_at)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Last Used:</span>
                  <span className="detail-value">{formatDate(cred.last_used_at)}</span>
                </div>
              </div>

              <div className="credential-actions">
                <button
                  className="btn-secondary"
                  onClick={() => handleVerify(cred.id)}
                  disabled={verifyingId === cred.id}
                >
                  {verifyingId === cred.id ? 'Verifying...' : 'Verify'}
                </button>
                <button
                  className="btn-danger"
                  onClick={() => handleDelete(cred.id, cred.label)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Credentials;
